import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { Button, Select } from "semantic-ui-react";
import VolumeMeter from "./VolumeMeter";
import { toggleMic } from "../../actions/micActions";

const Recorder = ({ onRecordingReady, recBtnRef }) => {
  const dispatch = useDispatch();
  const [isRecording, setIsRecording] = useState(false);
  const [recorder, setRecorder] = useState();
  const [currentMic, setCurrentMic] = useState();
  const [availableMicrophones, setAvailableMicrophones] = useState([]);
  const [maxVol, setMaxVol] = useState(-1);

  //   effects
  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      const microphones = devices.filter(
        (device) => device.kind === "audioinput"
      );
      setAvailableMicrophones(microphones);
      const defaultMic = getDefaultMicrophone(microphones);
      setCurrentMic(defaultMic);
      createRecorder(defaultMic.deviceId);
    });
    return () => {
      setRecorder(null);
    };
  }, []);

  //   handlers
  const startStop = () => {
    if (isRecording) stopRecording();
    else startRecording();
    setIsRecording(!isRecording);
    dispatch(toggleMic());
  };

  const startRecording = () => {
    recorder.onstart = modifyAudioGraph;
    recorder.ondataavailable = async (typedArray) => {
      const dataBlob = new Blob([typedArray], { type: "audio/ogg" });
      onRecordingReady(dataBlob);
    };
    recorder.start();
  };

  const stopRecording = () => {
    recorder.stop();
    setMaxVol(-1);
  };

  const createRecorder = (deviceId) => {
    const config = recorderConfigTemplate;
    config.mediaTrackConstraints.deviceId = deviceId;
    setRecorder(new window.Recorder(config));
    localStorage.setItem("microphone", deviceId);
  };

  const modifyAudioGraph = async () => {
    const recordingGainNode = recorder.recordingGainNode;
    const audioContext = recordingGainNode.context;
    const scriptNode = audioContext.createScriptProcessor(
      recorderConfigTemplate.bufferLength,
      recorderConfigTemplate.numberOfChannels,
      recorderConfigTemplate.numberOfChannels
    );
    scriptNode.connect(audioContext.destination);
    recordingGainNode.connect(scriptNode);
    scriptNode.onaudioprocess = processRecording;
  };

  const processRecording = (audioProcessingEvent) => {
    const inputBuffer = audioProcessingEvent.inputBuffer;
    const inputData = inputBuffer.getChannelData(0);
    setMaxVol(Math.max(...inputData.map(Math.abs)));
  };

  return (
    <div style={recorderStyle}>
      <Button
        icon="microphone"
        circular
        color="red"
        size="massive"
        onClick={startStop}
        basic={!isRecording}
        ref={recBtnRef}
      />
      <VolumeMeter maxVol={maxVol} numBars={10} width="20" height="60" />
      {currentMic && currentMic.label && (
        <Select
          style={{ marginLeft: "10px" }}
          options={availableMicrophones.map((mic, i) => ({
            key: i,
            value: mic.deviceId,
            text: mic.label,
          }))}
          defaultValue={currentMic.deviceId}
          onChange={(e, { value }) => createRecorder(value)}
        />
      )}
    </div>
  );
};

Recorder.propTypes = {
  onRecordingReady: PropTypes.func.isRequired,
};

const getDefaultMicrophone = (devices) => {
  if (!devices.length) {
    return null;
  }
  let savedMicId = localStorage.getItem("microphone");
  // if saved microphone no longer available than delete it from local storage
  if (
    savedMicId &&
    !devices.some((d) => {
      return d.deviceId === savedMicId;
    })
  ) {
    savedMicId = null;
    localStorage.removeItem("microphone");
  }

  const defaultMic = savedMicId
    ? devices.find((d) => d.deviceId === savedMicId)
    : devices[0];
  return defaultMic;
};

const recorderConfigTemplate = {
  bufferLength: 4096,
  encoderApplication: 2049,
  encoderFrameSize: 20,
  encoderPath: "/opus-recorder/encoderWorker.min.js",
  encoderSampleRate: 48000,
  encoderBitRate: 196000,
  maxFramesPerPage: 40,
  mediaTrackConstraints: {
    autoGainControl: false,
    echoCancellation: false,
    noiseSuppression: false,
    deviceId: null,
  },
  monitorGain: 0,
  numberOfChannels: 2,
  recordingGain: 1,
  resampleQuality: 3,
  streamPages: false,
  reuseWorker: false,
  wavBitDepth: 16,
};

const recorderStyle = {
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "flex-start",
};

export default Recorder;
